/**
 * AskOmegaFAB — iter 147
 * Always-on floating "Ask Omega" button + lightweight modal chat.
 * Reuses the existing /api/zi/chat (with iter 146 grounding) backend.
 * Tap anywhere on the dashboard → ask Omega about anything.
 * data-testid coverage: ask-omega-fab, ask-omega-input, ask-omega-send,
 * ask-omega-modal, ask-omega-close, ask-omega-bubble-*, ask-omega-followup-*
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import OmegaRichMessage from "./OmegaRichMessage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// This POST already sent credentials but never the CSRF header, so it 401'd
// on the CSRF check (403, technically) once the cookie was actually present.
// CSRF token: login/auth-me responses hand it back in the JSON body, stashed
// in localStorage under this key by App.js.
function csrfHeaders() {
  try {
    const stored = localStorage.getItem("roi_csrf_token");
    if (stored) return { "X-CSRF-Token": stored };
  } catch {}
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
}

const SESSION_KEY = "omega_fab_session_v1";

function makeSessionId() {
  return `fab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function loadSession() {
  try {
    const cached = localStorage.getItem(SESSION_KEY);
    if (cached) return cached;
  } catch (_) {}
  const fresh = makeSessionId();
  try { localStorage.setItem(SESSION_KEY, fresh); } catch (_) {}
  return fresh;
}

// Parse out trailing ```followups [...]``` block so the chat can render
// suggested next questions.
function splitFollowups(raw) {
  const m = (raw || "").match(/```followups\s*([\s\S]*?)```/);
  if (!m) return { text: raw, followups: [] };
  let arr = [];
  try { arr = JSON.parse(m[1].trim()); } catch (_) {}
  const text = raw.replace(/```followups[\s\S]*?```/, "").trim();
  return { text, followups: Array.isArray(arr) ? arr.slice(0, 3) : [] };
}

export default function AskOmegaFAB() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const sessionRef = useRef(loadSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, msgs, loading]);

  const send = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    setErr(null);
    setLoading(true);
    setMsgs(prev => [...prev, { role: "user", content: text }, { role: "assistant", content: "", streaming: true }]);
    setInput("");
    let full = "";
    try {
      const resp = await fetch(`${API}/zi/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...csrfHeaders() },
        body: JSON.stringify({ message: text, sessionId: sessionRef.current }),
        credentials: "include",
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.startsWith("data: ")) continue;
          try {
            const d = JSON.parse(ln.slice(6));
            if (d.chunk) {
              full += d.chunk;
              setMsgs(prev => { const a = [...prev]; a[a.length - 1] = { role: "assistant", content: full, streaming: true }; return a; });
            }
            if (d.done) {
              const { text: cleanText, followups } = splitFollowups(full);
              setMsgs(prev => { const a = [...prev]; a[a.length - 1] = { role: "assistant", content: cleanText, followups, streaming: false }; return a; });
            }
            if (d.error) {
              setErr(d.error);
              setMsgs(prev => { const a = [...prev]; a[a.length - 1] = { role: "assistant", content: "", streaming: false }; return a; });
            }
          } catch {}
        }
      }
    } catch (e) {
      setErr(e?.message || "Omega is unreachable");
      setMsgs(prev => { const a = [...prev]; a[a.length - 1] = { role: "assistant", content: "", streaming: false }; return a; });
    } finally {
      setLoading(false);
    }
  }, [input, loading]);

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating button — visible only when modal is closed */}
      <button
        data-testid="ask-omega-fab"
        onClick={() => setOpen(true)}
        aria-label="Ask Omega"
        style={{
          display: open ? "none" : "flex",
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 9999,
          width: 56,
          height: 56,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          boxShadow: "0 8px 24px rgba(124,58,237,0.45), 0 2px 6px rgba(0,0,0,0.18)",
          fontSize: 22,
          fontWeight: 800,
          letterSpacing: 0.5,
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "inherit",
          transition: "transform .15s ease",
        }}
        onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.06)")}
        onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
      >
        Ω
      </button>

      {/* Modal — always mounted, hidden when closed (0ms open, preserves chat history) */}
      <div
      data-testid="ask-omega-modal"
      role="dialog"
      aria-label="Omega chat"
      aria-hidden={!open}
      style={{
        display: open ? "flex" : "none",
        position: "fixed",
        right: 20,
        bottom: 20,
        zIndex: 9999,
        width: "min(440px, calc(100vw - 32px))",
        height: "min(620px, calc(100vh - 32px))",
        background: "var(--bg, #0b0d14)",
        border: "1px solid rgba(167,139,250,0.32)",
        borderRadius: 18,
        boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
        flexDirection: "column",
        overflow: "hidden",
        color: "var(--text, #e2e8f0)",
        fontFamily: "inherit",
      }}
    >
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "12px 14px",
        borderBottom: "1px solid rgba(167,139,250,0.18)",
        background: "linear-gradient(180deg, rgba(167,139,250,0.08) 0%, transparent 100%)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 800, color: "#fff",
          }}>Ω</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800 }}>Ask Omega</div>
            <div style={{ fontSize: 9, color: "var(--muted, #94a3b8)", letterSpacing: 0.8 }}>GROUNDED · REAL DATA · NO MOCKS</div>
          </div>
        </div>
        <button
          data-testid="ask-omega-close"
          onClick={() => setOpen(false)}
          aria-label="Close"
          style={{
            width: 28, height: 28, borderRadius: 8,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "var(--muted, #94a3b8)",
            cursor: "pointer",
            fontSize: 14,
            fontFamily: "inherit",
          }}
        >×</button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{
        flex: 1,
        overflowY: "auto",
        padding: "14px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}>
        {msgs.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--muted, #94a3b8)", lineHeight: 1.6 }}>
            Ask anything — current events, ticker analysis, market math, strategy ideas. Omega will ground real news + regime + cross-asset cascade before answering.
            <div style={{ marginTop: 10, display: "grid", gap: 6 }}>
              {[
                "What's the latest with NVDA?",
                "How would an oil spike affect airlines?",
                "Explain Monte Carlo simulation",
              ].map((q, i) => (
                <button
                  key={i}
                  data-testid={`ask-omega-suggest-${i}`}
                  onClick={() => send(q)}
                  style={{
                    fontSize: 11.5, padding: "8px 10px", borderRadius: 8,
                    background: "rgba(167,139,250,0.08)",
                    border: "1px solid rgba(167,139,250,0.22)",
                    color: "var(--text)", cursor: "pointer", textAlign: "left",
                    fontFamily: "inherit",
                  }}
                >{q}</button>
              ))}
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} data-testid={`ask-omega-bubble-${m.role}-${i}`} style={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "88%",
            padding: "8px 12px",
            borderRadius: m.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
            background: m.role === "user" ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.04)",
            border: `1px solid ${m.role === "user" ? "rgba(167,139,250,0.32)" : "rgba(255,255,255,0.08)"}`,
            fontSize: 12.5,
            lineHeight: 1.6,
            whiteSpace: m.role === "user" ? "pre-wrap" : undefined,
            color: "var(--text)",
          }}>
            {m.role === "assistant"
              ? <>
                  <OmegaRichMessage content={m.content || (m.streaming ? "…" : "")} charts={[]} />
                  {m.streaming && <span style={{ display: "inline-block", width: 6, height: 12, marginLeft: 2, background: "var(--muted, #94a3b8)", verticalAlign: "middle", animation: "fabBlink 1.1s infinite" }} />}
                </>
              : m.content
            }
            {!m.streaming && m.followups && m.followups.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
                {m.followups.map((q, j) => (
                  <button
                    key={j}
                    data-testid={`ask-omega-followup-${i}-${j}`}
                    onClick={() => send(q)}
                    style={{
                      fontSize: 10, padding: "3px 8px", borderRadius: 999,
                      background: "rgba(167,139,250,0.14)",
                      border: "1px solid rgba(167,139,250,0.32)",
                      color: "#a78bfa", cursor: "pointer", fontFamily: "inherit",
                    }}
                  >{q}</button>
                ))}
              </div>
            )}
          </div>
        ))}
        {err && (
          <div data-testid="ask-omega-error" style={{ alignSelf: "flex-start", fontSize: 11, color: "#f87171", padding: 6 }}>{err}</div>
        )}
      </div>

      <style>{`@keyframes fabBlink { 0%,100%{opacity:0.2;} 50%{opacity:1;} }`}</style>
      {/* Input */}
      <div style={{
        padding: 10,
        borderTop: "1px solid rgba(167,139,250,0.18)",
        display: "flex", gap: 6,
      }}>
        <textarea
          data-testid="ask-omega-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask Omega…"
          rows={1}
          style={{
            flex: 1,
            padding: "8px 10px",
            borderRadius: 10,
            fontSize: 12.5,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "var(--text)",
            outline: "none",
            fontFamily: "inherit",
            resize: "none",
          }}
        />
        <button
          data-testid="ask-omega-send"
          onClick={() => send()}
          disabled={loading || !input.trim()}
          style={{
            padding: "8px 14px",
            borderRadius: 10,
            fontSize: 11.5,
            fontWeight: 800,
            background: loading || !input.trim() ? "rgba(167,139,250,0.32)" : "#a78bfa",
            color: "#fff",
            border: "none",
            cursor: loading ? "wait" : (!input.trim() ? "not-allowed" : "pointer"),
            fontFamily: "inherit",
            letterSpacing: 0.4,
          }}
        >SEND</button>
      </div>
    </div>
    </>
  );
}
